/**
 * Wishlist — lightweight favorites stored in localStorage.
 *
 * - `toggleWishlist(product)` toggles a product in the wishlist and re-renders
 *   the drawer wishlist panel + all heart buttons on the page.
 * - The cart drawer has two views (cart / wishlist) controlled by the
 *   segmented toggle in the drawer header (`[data-drawer-view]`).
 * - Wishlist items are fetched from `/products/<handle>.js` so we always have
 *   fresh price / variant data.
 */

const WISHLIST_STORAGE_KEY = 'garabato_wishlist_v1';

function getWishlist() {
  try {
    const raw = localStorage.getItem(WISHLIST_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function setWishlist(items) {
  try {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* storage unavailable — ignore */
  }
}

function isInWishlist(productId) {
  return getWishlist().some((item) => String(item.id) === String(productId));
}

function toggleWishlist(product) {
  const items = getWishlist();
  const exists = items.some((item) => String(item.id) === String(product.id));
  const next = exists ? items.filter((item) => String(item.id) !== String(product.id)) : [...items, product];
  setWishlist(next);
  document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { productId: product.id } }));
  return !exists;
}

async function fetchWishlistProducts() {
  const ids = getWishlist();
  const products = [];
  for (const item of ids) {
    if (!item.handle) continue;
    try {
      const res = await fetch(`/products/${item.handle}.js`);
      if (res.ok) products.push(await res.json());
    } catch {
      /* skip unavailable product */
    }
  }
  return products;
}

function formatPrice(cents, currencyCode) {
  try {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: currencyCode || 'COP',
      maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(0)}`;
  }
}

function syncHeartButtons() {
  const wishlist = getWishlist();
  document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
    const id = btn.dataset.wishlistToggle;
    const active = wishlist.some((item) => String(item.id) === String(id));
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', String(active));
    btn.setAttribute('aria-label', active ? 'Quitar de deseos' : 'Agregar a deseos');
  });
}

document.addEventListener('wishlist:change', syncHeartButtons);

class WishlistDrawer {
  constructor(drawerRoot) {
    this.root = drawerRoot;
    // Toggle buttons live in the drawer header (outside this element), so query globally.
    this.toggleButtons = Array.from(document.querySelectorAll('[data-drawer-view]'));
    this.cartView = this.root.querySelector('[data-drawer-panel="cart"]');
    this.wishlistView = this.root.querySelector('[data-drawer-panel="wishlist"]');
    this.wishlistItemsEl = this.root.querySelector('[data-wishlist-items]');
    this.wishlistEmptyEl = this.root.querySelector('[data-wishlist-empty]');
    this.wishlistCountEl = this.root.querySelector('[data-wishlist-count]');

    this.toggleButtons.forEach((btn) => {
      if (btn.dataset.viewBound === 'true') return;
      btn.dataset.viewBound = 'true';
      btn.addEventListener('click', () => this.setView(btn.dataset.drawerView));
    });

    document.addEventListener('wishlist:change', () => {
      this.renderWishlist();
      syncHeartButtons();
    });
  }

  setView(view) {
    const isWishlist = view === 'wishlist';
    this.toggleButtons.forEach((btn) => {
      const active = btn.dataset.drawerView === view;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', String(active));
    });
    if (this.cartView) this.cartView.hidden = isWishlist;
    if (this.wishlistView) this.wishlistView.hidden = !isWishlist;
    if (isWishlist) this.renderWishlist();
  }

  async renderWishlist() {
    if (!this.wishlistItemsEl) return;
    const items = getWishlist();
    if (this.wishlistCountEl) {
      this.wishlistCountEl.textContent = String(items.length);
      this.wishlistCountEl.classList.toggle('visually-hidden', items.length === 0);
    }

    if (items.length === 0) {
      this.wishlistItemsEl.innerHTML = '';
      if (this.wishlistEmptyEl) this.wishlistEmptyEl.hidden = false;
      return;
    }
    if (this.wishlistEmptyEl) this.wishlistEmptyEl.hidden = true;

    const products = await fetchWishlistProducts();
    if (products.length === 0) {
      this.wishlistItemsEl.innerHTML = '';
      if (this.wishlistEmptyEl) this.wishlistEmptyEl.hidden = false;
      return;
    }

    this.wishlistItemsEl.innerHTML = products
      .map((p) => {
        const variant = p.selected_or_first_available_variant || p.variants?.[0];
        const img = p.featured_image || p.images?.[0];
        const imgUrl = img ? `https:${img.src}` : '';
        return `
          <div class="cart-wishlist__item" data-wishlist-item="${p.id}">
            <a class="cart-wishlist__media" href="/products/${p.handle}" tabindex="-1" aria-hidden="true">
              ${imgUrl ? `<img src="${imgUrl}" alt="${(p.title || '').replace(/"/g, '&quot;')}" loading="lazy">` : ''}
            </a>
            <div class="cart-wishlist__info">
              <a class="cart-wishlist__title" href="/products/${p.handle}">${p.title || ''}</a>
              <div class="cart-wishlist__price">${formatPrice(variant?.price || 0, 'COP')}</div>
              <button type="button" class="cart-wishlist__remove" data-wishlist-remove="${p.id}" aria-label="Quitar de deseos">
                <span class="svg-wrapper">${heartIcon()}</span>
              </button>
            </div>
            <button type="button" class="button button-primary cart-wishlist__add" data-wishlist-add="${variant?.id || ''}" ${variant ? '' : 'disabled'}>
              <span class="button-text">Agregar al carrito</span>
            </button>
          </div>
        `;
      })
      .join('');

    this.wishlistItemsEl.querySelectorAll('[data-wishlist-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const productId = btn.dataset.wishlistRemove;
        const next = getWishlist().filter((item) => String(item.id) !== String(productId));
        setWishlist(next);
        document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { productId } }));
      });
    });

    this.wishlistItemsEl.querySelectorAll('[data-wishlist-add]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        if (!btn.dataset.wishlistAdd) return;
        btn.disabled = true;
        const label = btn.querySelector('.button-text');
        const original = label.textContent;
        label.textContent = 'Agregando…';
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: Number(btn.dataset.wishlistAdd), quantity: 1 }),
          });
          if (!res.ok) throw new Error('add failed');
          label.textContent = '✓ Agregado';
          window.dispatchEvent(new CustomEvent('cart:refresh'));
        } catch {
          label.textContent = original;
          btn.disabled = false;
        }
      });
    });
  }

  syncHeartButtons() {
    syncHeartButtons();
  }
}

function heartIcon() {
  return `<svg aria-hidden="true" class="icon icon-heart" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 17s-6.25-4.06-8-7.5C.75 6.94 2.4 4 5.1 4c1.8 0 3.1 1 3.9 2.2C9.8 5 11.1 4 12.9 4c2.7 0 4.35 2.94 3.1 5.5-1.75 3.44-8 7.5-8 7.5Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
  </svg>`;
}

function initWishlistButtons() {
  document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
    if (btn.dataset.wishlistBound === 'true') return;
    btn.dataset.wishlistBound = 'true';
    btn.addEventListener('click', () => {
      const payload = {
        id: btn.dataset.wishlistToggle,
        handle: btn.dataset.wishlistHandle || '',
        title: btn.dataset.wishlistTitle || '',
      };
      toggleWishlist(payload);
    });
  });
  syncHeartButtons();
}

if (!customElements.get('wishlist-drawer')) {
  class WishlistDrawerElement extends HTMLElement {
    connectedCallback() {
      if (this.dataset.hydrated === 'true') return;
      this.dataset.hydrated = 'true';
      this.drawer = new WishlistDrawer(this);
    }
  }
  customElements.define('wishlist-drawer', WishlistDrawerElement);
}

document.addEventListener('DOMContentLoaded', () => {
  initWishlistButtons();
  // Re-bind after section re-renders (product cards, etc.)
  new MutationObserver(() => initWishlistButtons()).observe(document.body, { childList: true, subtree: true });
});

/* --- Cart upsell: one-at-a-time horizontal carousel --- */
class CartUpsellCarousel {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-upsell-track]');
    this.prev = root.querySelector('[data-upsell-prev]');
    this.next = root.querySelector('[data-upsell-next]');
    this.slides = Array.from(root.querySelectorAll('[data-upsell-slide]'));
    this.index = 0;

    if (!this.track || this.slides.length <= 1) {
      if (this.prev) this.prev.hidden = true;
      if (this.next) this.next.hidden = true;
      return;
    }

    this.prev.addEventListener('click', () => this.go(this.index - 1));
    this.next.addEventListener('click', () => this.go(this.index + 1));
    this.update();
  }

  go(index) {
    this.index = Math.max(0, Math.min(this.slides.length - 1, index));
    this.update();
  }

  update() {
    this.slides.forEach((slide, i) => {
      slide.style.display = i === this.index ? '' : 'none';
    });
    this.prev.disabled = this.index === 0;
    this.next.disabled = this.index === this.slides.length - 1;
  }
}

function initUpsellCarousels() {
  document.querySelectorAll('[data-upsell-carousel]').forEach((root) => {
    if (root.dataset.carouselBound === 'true') return;
    root.dataset.carouselBound = 'true';
    new CartUpsellCarousel(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initUpsellCarousels();
  new MutationObserver(() => initUpsellCarousels()).observe(document.body, { childList: true, subtree: true });
});

export { toggleWishlist, getWishlist, isInWishlist, initWishlistButtons, CartUpsellCarousel };
